import { ArrowBack } from "@mui/icons-material";
import {
  Button,
  Checkbox,
  CircularProgress,
  Paper,
  Dialog,
  Typography,
} from "@mui/material";
import EmailModal from "../email/EmailModal";
import { EmailType } from "../../common/EmailDetails";
import { useNavigate } from "react-router-dom";
import { Formik, Form, FieldArray } from "formik";
import InputField from "../../common/FormFields/InputField";
import SelectField from "../../common/FormFields/SelectField";
import { Vessel } from "../../common/Vessel";
import { JobRating, JobRatingLabel } from "../../common/JobOrder";
import logo from "../../images/ts-logo-nobg.png";
import EvaluationAssessorAutocomplete from "./EvaluationAssessorAutocomplete";
import EvaluationCommentEditor from "./EvaluationCommentEditor";
import { EvaluationSignature as EvaluationSignatureInput } from "./EvaluationSignature";
import { useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  useAddSignatureMutation,
  useApproveEvaluationMutation,
  useArchiveEvaluationMutation,
  useGetEvaluationQuery,
  useRejectEvaluationMutation,
  useSubmitEvaluationMutation,
  useUpdateEvaluationMutation,
} from "../../redux/api/evaluation";
import { toast } from "react-toastify";
import MatrixModal from "./MatrixModal";
import "./UnlicensedEvaluationForm.scss";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useSelector } from "react-redux";
import { selectEmailsByVesselAndRating } from "../../redux/vessel/vesselEmailSlice";
import { selectCurrentProfile } from "../../redux/authentication/authSlice";
import { MarinerProfile } from "../../common/MarinerProfile";

enum UnlicensedEvaluationScore {
  Unsatisfactory = 1,
  Satisfactory = 2,
  Exceeds = 3,
}

/**
 * UnlicensedEvaluationForm
 * 
 * This component is a simplified version of the EvaluationForm,
 * designed for unlicensed crew members. It includes:
 * - Mariner Information section
 * - Analysis of Performance section (Unsatisfactory / Satisfactory / Exceeds)
 * - Skills and Overall Evaluation sections
 * - Comments for Assessor, Reviewer, and Employee
 */

const UnlicensedEvaluationForm = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { pathname } = location;
  const evaluationId = pathname.split("/").pop();
  const { data: evaluationFromApi, isLoading: isEvaluationLoading, refetch } =
    useGetEvaluationQuery(evaluationId ?? "", { skip: !evaluationId });
  const [archiveEvaluation] = useArchiveEvaluationMutation();
  const [approveEvaluation] = useApproveEvaluationMutation();
  const [rejectEvaluation] = useRejectEvaluationMutation();
  const formRef = useRef<HTMLDivElement>(null);
  const commentRef = useRef<HTMLDivElement>(null);
  const commentHeaderRef = useRef<HTMLDivElement>(null);
  const assessorSectionRef = useRef<HTMLDivElement>(null);
  const reviewerSectionRef = useRef<HTMLDivElement>(null);
  const employeeSectionRef = useRef<HTMLDivElement>(null);
  const getAssessorRating = (): string => {
    switch (evaluationFromApi?.status) {
      case "EVALUATION_INITIATED":
      case "ASSESSOR_1_EDIT":
      case "ASSESSOR_2_RETURN":
        return evaluationFromApi?.assessor1Title ?? "";
      case "ASSESSOR_1_SUBMIT":
      case "FINAL_REVIEWER_RETURN":
        return evaluationFromApi?.assessor2Title ?? "";

      default:
        return "";
    }
  };

  const userProfile : MarinerProfile = useSelector(selectCurrentProfile);
  const ccEmail = useSelector(selectEmailsByVesselAndRating(evaluationFromApi?.vessel?.id ?? 0,getAssessorRating()));

  const getInitialValues = () => {
    if (!isEvaluationLoading && evaluationFromApi) {
      const res: any = JSON.parse(JSON.stringify(evaluationFromApi));
      Object.keys(res || {}).forEach(
        (key) => (res[key] = res[key] === null ? "" : res[key])
      );
      return {
        mmcId: res.profile?.mmcId ?? "",
        firstName: res.profile?.firstName ?? "",
        middleName: res.profile?.middleName ?? "",
        lastName: res.profile?.lastName ?? "",
        rating: res.profile?.rating ?? "",
        vessel: res.vessel ?? {},
        fromDate: res.fromDate ?? "",
        toDate: res.toDate ?? "",
        reviewedWithMariner: res.reviewedWithMariner ?? false,
        scores: (res.scores ?? [])
          .filter((s: any) => s?.question)
          .map((s: any) => ({
            ...s,
            score:
              s.score === 1
                ? "Unsatisfactory"
                : s.score === 2
                  ? "Satisfactory"
                  : s.score === 3
                    ? "Exceeds"
                    : typeof s.score === "string"
                      ? s.score
                      : "-",
          }))
          .sort(
            (a: any, b: any) =>
              (a?.question?.position || 0) - (b?.question?.position || 0)
          ),
        assessor1: res.assessor1 ?? "",
        assessor2: res.assessor2 ?? "",
        assessor1Comment: res.assessor1Comment ?? "",
        assessor2Comment: res.assessor2Comment ?? "",
        evaluateeComment: res.evaluateeComment ?? "",
        hasPreviousWarnings: res.hasPreviousWarnings ?? undefined,
      };
    } else {
      return {
        mmcId: "",
        firstName: "",
        middleName: "",
        lastName: "",
        rating: "",
        vessel: "",
        fromDate: "",
        toDate: "",
        scores: [],
        skills: [],
        overallEvaluation: "",
        assessor1: "",
        assessor2: "",
        assessor1Comment: "",
        assessor2Comment: "",
        evaluateeComment: "",
        reviewedWithMariner: false,
      };
    }
  };

  const initialValues = getInitialValues();

  const [signatureFile, setSignatureFile] = useState("");
  const [signatureText, setSignatureText] = useState("");

  const [updateEvaluation] = useUpdateEvaluationMutation();
  const [submitEvaluation] = useSubmitEvaluationMutation();
  const [addSignature] = useAddSignatureMutation();

  const [confirmReject, setConfirmReject] = useState(false);
  const [note, setNote] = useState("");
  const [submittingEvaluation, setSubmittingEvaluation] = useState(false);

  // helper to build a standardized evaluation payload (used by multiple actions)
  const buildUpdatedEvaluationPayload = (values: any) => {
    let assessor1UserId = undefined;
    let assessor2UserId = undefined;
    if (values.assessor1?.id || evaluationFromApi?.assessor1UserId) {
      assessor1UserId = values.assessor1?.id || evaluationFromApi?.assessor1UserId;
    }
    if (values.assessor2?.id || evaluationFromApi?.assessor2UserId) {
      assessor2UserId = values.assessor2?.id || evaluationFromApi?.assessor2UserId;
    }
    return {
      vessel: values.vessel?.id ? values.vessel : undefined,
      fromDate: values.fromDate ? new Date(values.fromDate).toISOString() : undefined,
      toDate: values.toDate ? new Date(values.toDate).toISOString() : undefined,
      lastUpdatedDate: values.lastUpdatedDate,
      scores: values.scores?.map((s: any) => ({
        ...s,
        score:
          s.score === "Unsatisfactory"
            ? 1
            : s.score === "Satisfactory"
              ? 2
              : s.score === "Exceeds"
                ? 3
                : typeof s.score === "number"
                  ? s.score
                  : null,
      })),
      assessor1UserId: assessor1UserId,
      assessor1Comment: values.assessor1Comment,
      assessor2UserId: assessor2UserId,
      assessor2Comment: values.assessor2Comment,
      evaluateeComment: values.evaluateeComment,
      hasPreviousWarnings: values.hasPreviousWarnings,
      reviewedWithMariner: values.reviewedWithMariner,
    };
  };


  const handleUploadSignature = async (values: any) => {
    if (!evaluationId) {
      toast.error(<p>Missing evaluation ID.</p>, { position: "top-center" });
      return;
    }

    if (!signatureFile && !signatureText) {
      toast.error(<p>A signature is required.</p>, { position: "top-center" });
      return;
    }

    try {
      const formData = new FormData();
      if (signatureFile) {
        const signatureBlob = await (await fetch(signatureFile)).blob();
        formData.append("fileSignature", signatureBlob, "signature.png");
      }
      if (signatureText) {
        formData.append("writtenSignature", signatureText);
      }

      const result = await addSignature({ id: evaluationId, file: formData });
      if ("error" in result) {
        toast.error(<p>Failed to upload signature.</p>, { position: "top-center" });
        return;
      }
      refetch();
      toast.success(<p>Final evaluation signed successfully.</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    } catch (error) {
      console.error("Error uploading signature:", error);
      toast.error(<p>Failed to sign evaluation. Please try again.</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    }
  };

  const handleSubmitEvaluation = async (values: any) => {
    if (!evaluationId) {
      toast.error(<p>Missing evaluation ID.</p>, { position: "top-center" });
      return;
    }

    if (!signatureFile && !signatureText) {
      toast.error(<p>A signature is required before submitting.</p>, {
        position: "top-center",
      });
      return;
    }

    try {
      setSubmittingEvaluation(true);
      // Step 1: Save current form data before submitting
      const savePayload = buildUpdatedEvaluationPayload(values);
      await updateEvaluation({ id: evaluationId, evaluation: savePayload }).unwrap();

      // Step 2: Prepare signature data
      const formData = new FormData();
      if (signatureFile) {
        const signatureBlob = await (await fetch(signatureFile)).blob();
        formData.append("fileSignature", signatureBlob, "signature.png");
      }
      if (signatureText) {
        formData.append("writtenSignature", signatureText);
      }

      // Append evaluation data
      const evaluationPayload = {
        vessel: values.vessel?.id ? values.vessel : undefined,
        fromDate: values.fromDate,
        toDate: values.toDate,
        scores: values.scores,
        assessor1Comment: values.assessor1Comment,
        assessor2Comment: values.assessor2Comment,
        evaluateeComment: values.evaluateeComment,
      };
      formData.append("evaluation", new Blob([JSON.stringify(evaluationPayload)], { type: "application/json" }));

      // Submit combined form data
      await submitEvaluation({ id: evaluationId, file: formData }).unwrap();
      refetch();
      toast.success(<p>Evaluation submitted successfully</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    } catch (error) {
      console.error("Error submitting evaluation:", error);
      toast.error(<p>Failed to submit evaluation. Please try again.</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    } finally {
      setSubmittingEvaluation(false);
    }
  };

  const handleArchiveEvaluation = async () => {
    if (!evaluationId) {
      toast.error(<p>Missing evaluation ID.</p>, { position: "top-center" });
      return;
    }
    try {
      await archiveEvaluation({ id: evaluationId }).unwrap();
      toast.success(<p>Evaluation archived successfully.</p>, { position: "top-center" });
      navigate(-1);
    } catch (error) {
      console.error("Error archiving evaluation:", error);
      toast.error(<p>Failed to archive evaluation.</p>, { position: "top-center" });
    }
  };

  const handleRejectEvaluation = async () => {
    if (!evaluationId) {
      toast.error(<p>Missing evaluation ID.</p>, { position: "top-center" });
      return;
    }
    try {
      const formData = new FormData();
      formData.append("rejectionNote", note || "Rejected by reviewer");
      await rejectEvaluation({ id: evaluationId, note: formData }).unwrap();
      setConfirmReject(false);
      toast.success(<p>Evaluation rejected successfully.</p>, { position: "top-center" });
    } catch (error) {
      console.error("Error rejecting evaluation:", error);
      toast.error(<p>Failed to reject evaluation.</p>, { position: "top-center" });
    }
  };

  const getReminderEmail = (values: any) => {
    switch (evaluationFromApi?.status) {
      case "EVALUATION_INITIATED":
      case "ASSESSOR_1_EDIT":
      case "ASSESSOR_2_RETURN":
        return values?.assessor1?.email;
      case "ASSESSOR_1_SUBMIT":
      case "FINAL_REVIEWER_RETURN":
        return values?.assessor2?.email;
      default:
        return "";
    }
  };

  const handleEmailCallBack = () => {
    toast.success(<p>Email sent successfully.</p>, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });
  };

  const handleApproveEvaluation = async (values: any) => {
    if (!evaluationId) {
      toast.error(<p>Missing evaluation ID.</p>, { position: "top-center" });
      return;
    }

    if (
      evaluationFromApi?.permissions?.assessor2 &&
      ["ASSESSOR_1_SUBMIT", "FINAL_REVIEWER_RETURN"].includes(evaluationFromApi?.status || "") &&
      !values.reviewedWithMariner
    ) {
      toast.error(
        <p>You must confirm that you have reviewed this evaluation with the mariner before approving.</p>,
        { position: "top-center" }
      );
      return;
    }

    try {
      const savePayload = buildUpdatedEvaluationPayload(values);
      await updateEvaluation({ id: evaluationId, evaluation: savePayload }).unwrap();

      const formData = new FormData();
      if (signatureFile) {
        const signatureBlob = await (await fetch(signatureFile)).blob();
        formData.append("fileSignature", signatureBlob, "signature.png");
      }
      if (signatureText) {
        formData.append("writtenSignature", signatureText);
      }
      await approveEvaluation({ id: evaluationId, file: formData }).unwrap();
      refetch();
      toast.success(<p>Evaluation approved successfully.</p>, { position: "top-center" });
    } catch (error) {
      console.error("Error approving evaluation:", error);
      toast.error(<p>Failed to approve evaluation.</p>, { position: "top-center" });
    }
  };

  const isEditable = () => !!evaluationFromApi?.permissions?.canEdit;
  const isScoreable = () => !!(isEditable() && evaluationFromApi?.permissions?.assessor1);
  const isCommentable = () => !!evaluationFromApi?.permissions?.canComment;
  const isSubmittable = () => !!evaluationFromApi?.permissions?.canSubmit;
  const isApprovable = () => !!evaluationFromApi?.permissions?.canApprove;
  const isSignable = () => !!evaluationFromApi?.permissions?.canSign;
  const isArchivable = () => !!evaluationFromApi?.permissions?.canArchive;
  const isRemindable = () => !!evaluationFromApi?.permissions?.canRemind;
  const isReassignable = (assessorNumber: number) => {
    if (assessorNumber === 1) return !!evaluationFromApi?.permissions?.canReassignAssessor1;
    if (assessorNumber === 2) return !!evaluationFromApi?.permissions?.canReassignAssessor2;
    return false;
  };

  const isEvaluatee = () => !!evaluationFromApi?.permissions?.evaluatee;

  const handleExport = async () => {
    if (!formRef.current) return;

    const fixedWidth = 2056;
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    pdf.deletePage(1);

    const HEADER_HEIGHT_MM = 8;

    await addRefToPdf(formRef.current, pdf, fixedWidth, pdfWidth, pdfHeight, 0, HEADER_HEIGHT_MM);

    // Comment sections always start on a fresh page; currentPageY=0 forces that
    let currentPageY = 0;

    const commentSectionRefs = [
      commentHeaderRef,
      assessorSectionRef,
      reviewerSectionRef,
      employeeSectionRef,
    ];

    for (const ref of commentSectionRefs) {
      if (ref.current) {
        currentPageY = await addRefToPdf(ref.current, pdf, fixedWidth, pdfWidth, pdfHeight, currentPageY, HEADER_HEIGHT_MM);
      }
    }

    await addHeaders(pdf, HEADER_HEIGHT_MM);
    addFooters(pdf);

    pdf.save(
      `evaluation${evaluationFromApi?.evaluatee
        ? `_${evaluationFromApi.evaluatee.firstName}${evaluationFromApi.evaluatee.middleName ?? ""
        }${evaluationFromApi.evaluatee.lastName}`
        : ""
      }.pdf`
    );
  };

  const addRefToPdf = async (
    ref: HTMLDivElement,
    pdf: jsPDF,
    width: number,
    pdfWidth: number,
    pdfHeight: number,
    currentPageY: number = 0,
    headerHeightMm: number = 0
  ): Promise<number> => {
    return html2canvas(ref, {
      windowWidth: width,
      onclone: (document) => {
        const evalForm = document.getElementById("unlicensed-eval-form");
        if (evalForm) {
          evalForm.style.paddingLeft = "40px";
          evalForm.style.paddingRight = "60px";
          evalForm.style.paddingTop = "40px";
          evalForm.style.width = "1550px";
        }

        const commentSection = document.getElementById("comments-section");
        if (commentSection) {
          commentSection.style.paddingLeft = "40px";
          commentSection.style.paddingRight = "60px";
          commentSection.style.paddingTop = "40px";
          commentSection.style.width = "1850px";
        }

        // Apply matching margins to individually-rendered comment sections
        document.querySelectorAll("[data-pdf-section]").forEach((el) => {
          const section = el as HTMLElement;
          section.style.paddingLeft = "40px";
          section.style.paddingRight = "60px";
          section.style.boxSizing = "border-box";
          section.style.width = "1850px";
          if (section.dataset.pdfSection === "header") {
            section.style.paddingTop = "40px";
          }
        });

        // Logos are added as a jsPDF overlay on every page via addHeaders instead

        const evalScoreSection = document.getElementById("Score-section");
        if (evalScoreSection) {
          evalScoreSection.style.overflow = "visible";
          evalScoreSection.style.height = "auto";
        }

        const evalSection = document.getElementById("eval-section");
        if (evalSection) {
          evalSection.style.height = "auto";
        }

        // convert textareas to divs since html2canvas can't properly capture multi-line textarea
        const textareas = document.querySelectorAll("textarea");
        textareas.forEach((textarea) => {
          if (textarea.ariaHidden === "true") {
            textarea.setAttribute("data-html2canvas-ignore", "true");
          } else {
            const computedStyle = window.getComputedStyle(textarea);
            const div = document.createElement("div");
            div.textContent = textarea.value;
            div.style.cssText = `
                    width: ${textarea.clientWidth}px;
                    min-height: ${textarea.clientHeight}px;
                    max-height: 200px;
                    overflow:hidden;
                    border: ${computedStyle.border};
                    padding: ${computedStyle.padding};
                    font-family: ${computedStyle.fontFamily};
                    font-size: ${computedStyle.fontSize};
                    line-height: ${computedStyle.lineHeight};
                    color: ${computedStyle.color};
                    background: ${computedStyle.background};
                    white-space: pre-wrap;
                    word-wrap: break-word;
                  `;
            textarea.replaceWith(div);
          }
        });

        // Fix for sticky elements
        const fixedElements = document.querySelectorAll(
          ".sticky, .off, .mariner, .rating"
        );
        fixedElements.forEach((el) => {
          el.classList.remove("sticky", "off", "mariner", "rating");
        });
      },
      scale: 2,
      allowTaint: false,
    }).then((canvas) => {
      const leftMargin = 50;
      const canvasWidth = canvas.width - leftMargin;
      const canvasHeight = canvas.height;
      const pxPerMm = canvasWidth / pdfWidth;
      const contentHeightMm = canvasHeight / pxPerMm;

      // Usable page height after reserving space for the logo header
      const usablePdfHeight = pdfHeight - headerHeightMm;
      const pageHeight = (usablePdfHeight * canvasWidth) / pdfWidth;

      const findBreakPoint = (startY: number): number => {
        const remaining = canvasHeight - startY;
        if (remaining <= pageHeight) return remaining;

        const ctx = canvas.getContext("2d");
        if (!ctx) return pageHeight;

        const maxY = Math.min(startY + Math.floor(pageHeight), canvasHeight);
        const minY = Math.max(startY, maxY - Math.floor(pageHeight * 0.5));

        for (let y = maxY; y >= minY; y--) {
          const data = ctx.getImageData(0, y, canvasWidth, 1).data;
          let isBackground = true;
          for (let x = 0; x < data.length; x += 40) {
            if (data[x] < 200 || data[x + 1] < 200 || data[x + 2] < 200) {
              isBackground = false;
              break;
            }
          }
          if (isBackground) return y - startY;
        }
        return pageHeight;
      };

      // If the content fits in the remaining space on the current page, place it inline
      if (currentPageY > 0 && contentHeightMm <= pdfHeight - currentPageY) {
        const croppedCanvas = document.createElement("canvas");
        const croppedCtx = croppedCanvas.getContext("2d");
        croppedCanvas.width = canvasWidth;
        croppedCanvas.height = canvasHeight;
        croppedCtx?.drawImage(canvas, 0, 0, canvasWidth, canvasHeight, 0, 0, canvasWidth, canvasHeight);
        const imgHeight = (pdfWidth * canvasHeight) / canvasWidth;
        pdf.addImage(croppedCanvas.toDataURL("image/png"), "PNG", 0, currentPageY, pdfWidth, imgHeight);
        return currentPageY + imgHeight;
      }

      // Otherwise slice across new pages, each offset below the logo header
      let yPosition = 0;
      let lastSliceHeightPx = pageHeight;

      while (yPosition < canvasHeight) {
        const sliceHeight = findBreakPoint(yPosition);
        lastSliceHeightPx = sliceHeight;

        const pageCanvas = document.createElement("canvas");
        const pageCtx = pageCanvas.getContext("2d");
        pageCanvas.width = canvasWidth;
        pageCanvas.height = sliceHeight;

        if (!pageCtx) {
          console.error("Failed to get 2D context for page canvas.");
          return 0;
        }

        pageCtx.drawImage(canvas, 0, yPosition, canvasWidth, sliceHeight, 0, 0, canvasWidth, sliceHeight);
        const pageImgData = pageCanvas.toDataURL("image/png");

        pdf.addPage();
        pdf.addImage(pageImgData, "PNG", 0, headerHeightMm, pdfWidth, (pdfWidth * sliceHeight) / canvasWidth);

        yPosition += sliceHeight;
      }

      return headerHeightMm + lastSliceHeightPx / pxPerMm;
    });
  };

  const addHeaders = async (doc: jsPDF, headerHeightMm: number) => {
    const pageCount = doc.internal.pages.length - 1;
    const img = new Image();
    img.src = logo;
    await new Promise<void>((resolve) => { img.onload = () => resolve(); });
    const aspectRatio = img.naturalWidth / img.naturalHeight;
    const logoH = headerHeightMm - 2;
    const logoW = logoH * aspectRatio;
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.addImage(img, "PNG", 5, 3, logoW, logoH);
    }
  };

  const addFooters = (doc: jsPDF) => {
    // account for the empty first page
    const pageCount = doc.internal.pages.length - 1;
    const footerText = "Performance Evaluation " + getEvaluationYear();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFont("Helvetica", "Bold");
      doc.setFontSize(9);
      doc.setTextColor(15, 46, 105);
      doc.text(footerText, 10, 290, {
        align: "left",
      });
      doc.text(
        String(i) + " of " + String(pageCount),
        doc.internal.pageSize.width - 20,
        290,
        {
          align: "right",
        }
      );
    }
  };

  const getEvaluationYear = () => {
    const evalYear = evaluationFromApi?.fromDate?.substring(0, 4);
    return !!evalYear ? evalYear : "2024"; // what should default year be?
  };

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      validate={(values) => {
        const errors: any = {};
        if (!values.fromDate) errors.fromDate = "From Date is required";
        if (!values.toDate) errors.toDate = "To Date is required";
        if (
          evaluationFromApi?.permissions?.assessor2 &&
          ["ASSESSOR_1_SUBMIT", "FINAL_REVIEWER_RETURN"].includes(evaluationFromApi?.status || "") &&
          !values.reviewedWithMariner
        ) {
          errors.reviewedWithMariner = "You must confirm that you have reviewed this evaluation with the mariner before submitting.";
        }
        return errors;
      }}
      onSubmit={async (values) => {
        try {
          if (!evaluationId) {
            alert("Missing evaluation ID.");
            return;
          }
          const payload = buildUpdatedEvaluationPayload(values);
          await updateEvaluation({ id: evaluationId, evaluation: payload }).unwrap();
          refetch();

          toast.success(<p>Evaluation updated successfully.</p>, {
            position: "top-center",
            autoClose: 5000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
          });
        } catch (error) {
          console.error("Error updating evaluation:", error);
          toast.error(<p>Failed to update evaluation. Please try again.</p>, {
            position: "top-center",
            autoClose: 5000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
          });
        }
      }}
    >
      {({ isSubmitting, values, setFieldValue, setFieldTouched }) => (
        <Form id="unlicensed-evaluation-form" style={{ marginTop: "-30px" }}>
          <div className="eval-form">
            <div ref={formRef} id="unlicensed-eval-form">
              <div className="page-header bg-white align-items-center sticky justify-content-btwn mb-0 pb-2 pt-3">
                <div className="flex g-3 align-items-center justify-content-btwn w-100">
                  <div className="flex g-3 align-items-center">
                    <img className="pdf-logo-size" id="pdf-logo" src={logo} alt="logo" />
                    <h1>Unlicensed Crew Evaluation</h1>
                    <div className="flex align-items-center p-2" data-html2canvas-ignore>
                      {(isEditable() || isCommentable() || isReassignable(1) || isReassignable(2)) && (
                        <Button
                          variant="contained"
                          className="blue-button mr-2"
                          type="submit"
                          disabled={isSubmitting || submittingEvaluation}
                        >
                          Save Changes
                        </Button>
                      )}
                      {isArchivable() && (
                        <Button
                          variant="contained"
                          className="blue-button mr-2"
                          onClick={() => handleArchiveEvaluation()}
                          disabled={isSubmitting || submittingEvaluation}
                        >
                          Archive
                        </Button>
                      )}
                      {isSubmittable() && (
                        <Button
                          variant="contained"
                          className="blue-button mr-2"
                          onClick={() => handleSubmitEvaluation(values)}
                          disabled={isSubmitting || submittingEvaluation}
                        >
                          Sign & Submit
                        </Button>
                      )}
                      {isApprovable() && (
                        <>
                          <Button
                            variant="contained"
                            className="blue-button mr-2"
                            onClick={() => handleApproveEvaluation(values)}
                            disabled={isSubmitting || submittingEvaluation}
                          >
                            Sign & Approve
                          </Button>
                          <Button
                            variant="contained"
                            className="blue-button mr-2"
                            onClick={() => setConfirmReject(true)}
                            disabled={isSubmitting || submittingEvaluation}
                          >
                            Reject
                          </Button>
                          <Dialog
                            open={confirmReject}
                            onClose={() => setConfirmReject(false)}
                            aria-labelledby="modal-title"
                          >
                            <Typography variant="h3" id="modal-title">
                              Reject Evaluation
                            </Typography>
                            <p className="delete-paragraph">
                              Are you sure you would like to reject this Evaluation?
                            </p>
                            <label>
                              <span>Note:</span>
                              <textarea
                                id="rejectionNote"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                maxLength={1000}
                              />
                            </label>
                            <div className="modal-footer">
                              <Button
                                className="outline-button-small"
                                onClick={() => setConfirmReject(false)}
                              >
                                Cancel
                              </Button>
                              <Button
                                className="blue-button-small mr-2"
                                onClick={() => handleRejectEvaluation()}
                              >
                                Reject
                              </Button>
                            </div>
                          </Dialog>
                        </>
                      )}
                      {isRemindable() && (
                        <EmailModal
                          key={EmailType.EVALUATION_ALERT}
                          email={getReminderEmail(values)}
                          emailType={EmailType.EVALUATION_ALERT}
                          parentCallback={handleEmailCallBack}
                          emailData={{
                            vesselName: evaluationFromApi?.vessel?.name!,
                            rating:
                              JobRatingLabel[
                              evaluationFromApi?.rating! || evaluationFromApi?.profile?.rating!
                              ],
                            marinerName: [
                              evaluationFromApi?.profile?.firstName,
                              evaluationFromApi?.profile?.middleName,
                              evaluationFromApi?.profile?.lastName,
                            ].join(" "),
                            evalLink: `${window.location.origin}/evaluations/${evaluationFromApi?.id}`,
                          }}
                          emailSubject={`ACTION: Performance Evaluation for ${[
                            evaluationFromApi?.profile?.firstName,
                            evaluationFromApi?.profile?.middleName,
                            evaluationFromApi?.profile?.lastName,
                          ].join(" ")}`}
                          cc={ccEmail}
                        >
                          <Button variant="contained" className="blue-button">
                            Remind
                          </Button>
                        </EmailModal>
                      )}
                      {isSignable() &&
                                !!userProfile?.id &&
                                userProfile.id === evaluationFromApi?.evaluatee?.id && (
                                  <Button
                                    variant="contained"
                                    className="blue-button mr-2"
                                    onClick={() => handleUploadSignature(values)}
                                    disabled={submittingEvaluation}
                                  >
                                    Sign & Submit
                                  </Button>
                      )}
                      <div data-html2canvas-ignore>
                        <MatrixModal evaluationType="unlicensed" />
                      </div>
                      <Button
                      variant="contained"
                      className="blue-button mr-2"
                      onClick={() => handleExport()}
                    >
                      Export
                    </Button>
                    </div>
                  </div>
                </div>
                <Button
                  data-html2canvas-ignore
                  className="blue-link-button"
                  onClick={() => navigate(-1)}
                >
                  <ArrowBack />
                  Back
                </Button>
              </div>

              {isSubmitting && <CircularProgress size={24} />}

              <Paper>
                <div className="registration-header mb-1">
                  <h3 className="ft-18">Mariner Information</h3>
                </div>
                <div className="form-wide">
                  <label>
                    <span className="required">MMC ID</span>
                    <InputField name="mmcId" fullWidth size="small" type="text" disabled />
                  </label>
                  <label>
                    <span className="required">First Name</span>
                    <InputField name="firstName" fullWidth size="small" type="text" disabled />
                  </label>
                  <label>
                    <span>Middle Name</span>
                    <InputField name="middleName" fullWidth size="small" type="text" disabled />
                  </label>
                  <label>
                    <span className="required">Last Name</span>
                    <InputField name="lastName" fullWidth size="small" type="text" disabled />
                  </label>
                  <label>
                    <span>Rating</span>
                    <InputField
                      name="rating"
                      fullWidth
                      size="small"
                      type="text"
                      disabled
                      value={
                        JobRatingLabel[
                        JobRating[values.rating as keyof typeof JobRating]
                        ] ?? ""
                      }
                    />
                  </label>
                  <label>
                    <span className="required">Vessel</span>
                    <InputField
                      name="vessel"
                      value={
                        typeof values.vessel === "string"
                          ? values.vessel
                          : (values.vessel as Vessel)?.name ?? ""
                      }
                      disabled
                    />
                  </label>
                  <label>
                    <span className="required">From Date</span>
                    <InputField
                      name="fromDate"
                      fullWidth
                      size="small"
                      type="date"
                      disabled={!isEditable()}
                      required
                    />
                  </label>
                  <label>
                    <span className="required">To Date</span>
                    <InputField
                      name="toDate"
                      fullWidth
                      size="small"
                      type="date"
                      disabled={!isEditable()}
                      required
                    />
                  </label>
                </div>
              </Paper>

              <div className="eval-section" id="eval-section">
                <Paper className="analysis-desc flex-1">
                  <div className="registration-header">
                    <h3 className="ft-18">Analysis of Performance</h3>
                  </div>
                  <div className="flex-col g-1">
                    <p className="ft-14 mb-1">
                      This section is designed to serve as a constructive guide for analyzing the employee’s performance.
                      Read each category carefully and consider the extent to which the employee demonstrates the element
                      of performance described. Indicate your analysis of the employee’s performance by marking
                      <b> Unsatisfactory, Satisfactory, or Exceeds.</b>
                    </p>
                    <div className="flex-col g-0">
                      <h4>Unsatisfactory</h4>
                      <small className="ft-14">
                        Performance that does not meet position requirements, objectives and expectations on the vessel.
                        Immediate attention to improvement and/or action is required.
                      </small>
                    </div>
                    <div className="flex-col g-0">
                      <h4>Satisfactory</h4>
                      <small className="ft-14">
                        Performance that meets all of the position requirements on the vessel.
                      </small>
                    </div>
                    <div className="flex-col g-0">
                      <h4>Exceeds</h4>
                      <small className="ft-14">
                        Performance that exceeds normal job requirements.
                      </small>
                    </div>
                  </div>
                </Paper>

                <Paper className="flex-1">
                  <FieldArray
                    name="scores"
                    render={(arrayHelpers) => (
                      <div
                        id="Score-section"
                        style={{ overflowY: "auto", height: "710px" }}
                      >
                        <div
                          className="registration-header"
                          style={{ marginBottom: "0px" }}
                        >
                          <h3 className="ft-18">
                            Performance Evaluation Questions
                          </h3>
                        </div>
                        {(values as any).scores &&
                          (values as any).scores.length > 0 &&
                          Object.entries(
                            ((values as any).scores as any[]).reduce(
                              (
                                acc: {
                                  [key: string]: {
                                    score: any;
                                    originalIndex: number;
                                  }[];
                                },
                                curr: any,
                                originalIndex: number
                              ) => {
                                const subSection = curr.question?.subSection || "";
                                if (!acc[subSection]) {
                                  acc[subSection] = [];
                                }
                                acc[subSection].push({
                                  score: curr,
                                  originalIndex,
                                });
                                return acc;
                              },
                              {}
                            )
                          ).map(([subSection, subSectionScores]) => (
                            <div key={subSection}>
                              <h3 className="sub-reg-header">{subSection}</h3>
                              {subSectionScores.map(
                                ({ score, originalIndex }) => (
                                  <div
                                    className="score-rows flex align-items-center justify-content-btwn"
                                    key={originalIndex}
                                  >
                                    <div className="score-question">
                                      <p className="b-600 mb-0">
                                        {score.question?.questionText}
                                      </p>
                                      <small className="ft-14">
                                        {score.question?.description}
                                      </small>
                                    </div>
                                    <div className="flex-row align-items-end g-1">
                                      <SelectField
                                        name={`scores.${originalIndex}.score`}
                                        label="Score"
                                        data={[
                                          { value: "-", label: "Select a score" },
                                          ...Object.entries(UnlicensedEvaluationScore)
                                            .filter(([key]) => isNaN(Number(key)))
                                            .map(([key]) => ({
                                              value: key,
                                              label: key,
                                            })),
                                        ]}
                                        fullWidth
                                        size="small"
                                        disabled={!isScoreable() || score.notApplicable}
                                      />
                                      <div className="flex-col align-items-center">
                                        <span className="text ft-12">N/A</span>
                                        <Checkbox
                                          className="eval-na-checkbox"
                                          checked={!!score?.notApplicable}
                                          onChange={() => {
                                            arrayHelpers.form.setFieldValue(
                                              `scores.${originalIndex}.notApplicable`,
                                              !score.notApplicable
                                            );
                                            arrayHelpers.form.setFieldValue(
                                              `scores.${originalIndex}.score`,
                                              "-"
                                            );
                                          }}
                                          size="small"
                                          disabled={!isScoreable()}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                )
                              )}
                            </div>
                          ))}
                        <div className="flex-col g-1 pt-3 border-top-light">
                          <div className="score-rows" style={{ display: "grid", gap: "16px", alignItems: "center", gridTemplateColumns: "1fr 100px" }}>
                            <div>
                              <p className="b-600">Previous warning(s) issued?</p>
                            </div>
                            <SelectField
                              name={"hasPreviousWarnings"}
                              data={[
                                { value: true, label: "Yes" },
                                { value: false, label: "No" },
                              ]}
                              fullWidth
                              size="small"
                              disabled={!isScoreable()}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  />
                </Paper>
              </div>
            </div>
            <Paper>
              <div id="comments-section" ref={commentRef}>
                <div ref={commentHeaderRef} data-pdf-section="header" className="registration-header flex-col align-items-start g-5px">
                  <div className="flex g-3 align-items-center">
                    <img
                      className="pdf-logo-size"
                      id="pdf-logo-comments"
                      src={logo}
                      alt=""
                    />
                    <h2 className="ft-20">Comments</h2>
                  </div>
                  <p>
                    *Comments are mandatory for any criteria marked Unsatisfactory or Exceeds
                  </p>
                </div>
                <div className="flex-col">
                  <div ref={assessorSectionRef} data-pdf-section="section" className="flex-col g-1 py-2 border-bottom-light">
                    <h2 className="ft-20">Assessor</h2>
                    <label>
                      <span className="required">Assessor Name</span>
                      <div className="signature-wrapper">
                        <EvaluationAssessorAutocomplete
                          id="assessor1.id"
                          vesselId={evaluationFromApi?.vessel?.id || 0}
                          value={{
                            userId: values?.assessor1?.id || "",
                            name: values?.assessor1?.name || "",
                            rating: values?.assessor1?.title || "",
                            email: values?.assessor1?.email || "",
                            mmcId: null
                          }}
                          disabled={evaluationFromApi === undefined || !isReassignable(1)}
                          onChange={(m) => {
                            setFieldValue("assessor1", {
                              id: m?.userId || undefined,
                              name: m?.name || undefined,
                              title: m?.rating || undefined,
                              email: m?.email || undefined
                            });
                          }}
                        />
                      </div>
                    </label>
                    <div className="flex-col g-1">
                      {evaluationFromApi?.permissions?.assessor1 &&
                        ["EVALUATION_INITIATED", "ASSESSOR_1_EDIT", "ASSESSOR_2_RETURN"].includes(
                          evaluationFromApi?.status || ""
                        ) && isCommentable() ? (
                        <div className="flex-col g-1">
                          <div className="signature-wrapper">
                            {isSignable() && (
                              <EvaluationSignatureInput
                                signatureFile={signatureFile}
                                setSignatureFile={setSignatureFile}
                                signatureText={signatureText}
                                setSignatureText={setSignatureText}
                              />
                            )}
                          </div>
                          <label>
                            <span className="required">Assessor Comment</span>
                            <EvaluationCommentEditor
                              initialValue={initialValues.assessor1Comment}
                              labelName="assessor1Comment"
                              labelHelperName="assessor1CommentHelper"
                              setFieldTouched={setFieldTouched}
                              setFieldValue={setFieldValue}
                            />
                          </label>
                        </div>
                      ) : (
                        <div className="flex-col g-1">
                          <div className="signature-wrapper">
                            {!!evaluationFromApi?.assessor1Signature && (
                              <img
                                alt="assessor-signature"
                                width="100%"
                                className="border p-2"
                                src={evaluationFromApi.assessor1Signature.imageBase64}
                              />
                            )}
                          </div>
                          <label>
                            <span>Assessor Comment</span>
                            <div>
                              <p
                                className="comment-ft-container"
                                dangerouslySetInnerHTML={{
                                  __html: values.assessor1Comment,
                                }}
                              />
                            </div>
                          </label>
                        </div>
                      )}
                    </div>
                  </div>

                  <div ref={reviewerSectionRef} data-pdf-section="section" className="flex-col g-1 py-2 border-bottom-light">
                    <h2 className="ft-20">Reviewer</h2>
                    <label>
                      <span className="required">Reviewer Name</span>
                      <div className="signature-wrapper">
                        <EvaluationAssessorAutocomplete
                          id="assessor2.id"
                          vesselId={evaluationFromApi?.vessel?.id || 0}
                          value={{
                            userId: values?.assessor2?.id || "",
                            name: values?.assessor2?.name || "",
                            rating: values?.assessor2?.title || "",
                            email: values?.assessor2?.email || "",
                            mmcId: null
                          }}
                          disabled={evaluationFromApi === undefined || !isReassignable(2)}
                          onChange={(m) => {
                            setFieldValue("assessor2", {
                              id: m?.userId || undefined,
                              name: m?.name || undefined,
                              title: m?.rating || undefined,
                              email: m?.email || undefined
                            });
                          }}
                        />
                      </div>
                    </label>
                    <div className="flex-col g-1">
                      {evaluationFromApi?.permissions?.assessor2 &&
                        ["ASSESSOR_1_SUBMIT", "FINAL_REVIEWER_RETURN"].includes(
                          evaluationFromApi?.status || ""
                        ) && isCommentable() ? (
                        <div className="flex-col g-1">
                          <div className="signature-wrapper">
                            {isSignable() && !isEvaluatee() && (
                              <EvaluationSignatureInput
                                signatureFile={signatureFile}
                                setSignatureFile={setSignatureFile}
                                signatureText={signatureText}
                                setSignatureText={setSignatureText}
                              />
                            )}
                          </div>
                          <label>
                            <span>Reviewer Comment</span>
                            <EvaluationCommentEditor
                              initialValue={initialValues.assessor2Comment}
                              labelName="assessor2Comment"
                              labelHelperName="assessor2CommentHelper"
                              setFieldTouched={setFieldTouched}
                              setFieldValue={setFieldValue}
                            />
                          </label>

                          <div className="reviewed-with-mariner-container">
                            <input
                              type="checkbox"
                              id="reviewedWithMariner"
                              name="reviewedWithMariner"
                              checked={!!values.reviewedWithMariner}
                              onChange={(e) => {
                                setFieldValue("reviewedWithMariner", e.target.checked);
                                setFieldTouched("reviewedWithMariner", true, false);
                              }}
                              disabled={!isEditable() && !evaluationFromApi?.permissions?.assessor2}
                              className="reviewed-with-mariner-checkbox"
                            />
                            <div className="reviewed-with-mariner-text">
                              <span className="b-600">Reviewed With Mariner</span>
                              <small className="ft-14">
                                Please confirm that you have reviewed this evaluation with the mariner.
                              </small>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex-col g-1">
                          <div className="signature-wrapper">
                            {!!evaluationFromApi?.assessor2Signature && (
                              <img
                                alt="reviewer-signature"
                                width="100%"
                                className="border p-2"
                                src={evaluationFromApi.assessor2Signature.imageBase64}
                              />
                            )}
                          </div>
                          <label>
                            <span>Reviewer Comment</span>
                            <div>
                              <p
                                className="comment-ft-container"
                                dangerouslySetInnerHTML={{
                                  __html: values.assessor2Comment,
                                }}
                              />
                            </div>
                          </label>

                          <div className="reviewed-with-mariner-container">
                            <div className="reviewed-with-mariner-text">
                              <span className="b-600">Reviewed With Mariner</span>
                              <small className="ft-14">
                                Please confirm that you have reviewed this evaluation with the mariner.
                              </small>
                            </div>
                            <input
                              type="checkbox"
                              id="reviewedWithMariner"
                              name="reviewedWithMariner"
                              checked={!!evaluationFromApi?.reviewedWithMariner}
                              readOnly
                              className="reviewed-with-mariner-checkbox"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div ref={employeeSectionRef} data-pdf-section="section" className="flex-col g-1 py-2">
                    <h2 className="ft-20">Employee</h2>
                    <label>
                      <span className="required">Employee Name</span>
                      <InputField
                        name="employeeName"
                        className="signature-wrapper"
                        size="small"
                        type="text"
                        disabled
                        value={`${values.firstName ?? ""} ${values.middleName ?? ""} ${values.lastName ?? ""}`.trim() || ""}
                      />
                    </label>
                    <div className="flex-col g-1">
                      <div className="signature-wrapper">
                        {evaluationFromApi?.permissions?.evaluatee &&
                          ["FINAL_REVIEWER_APPROVE"].includes(evaluationFromApi?.status || "") &&
                          isSignable() ? (
                          <EvaluationSignatureInput
                            signatureFile={signatureFile}
                            setSignatureFile={setSignatureFile}
                            signatureText={signatureText}
                            setSignatureText={setSignatureText}
                          />
                        ) : (
                          !!evaluationFromApi?.evaluateeSignature && (
                            <img
                              alt="employee-signature"
                              width="100%"
                              className="border p-2"
                              src={evaluationFromApi.evaluateeSignature.imageBase64}
                            />
                          )
                        )}
                      </div>
                      <label>
                        <span>Employee Comment</span>
                        {evaluationFromApi?.permissions?.evaluatee &&
                          ["FINAL_REVIEWER_APPROVE"].includes(evaluationFromApi?.status || "") &&
                          isCommentable() ? (
                          <EvaluationCommentEditor
                            initialValue={initialValues.evaluateeComment}
                            labelName="evaluateeComment"
                            labelHelperName="evaluateeCommentHelper"
                            setFieldTouched={setFieldTouched}
                            setFieldValue={setFieldValue}
                          />
                        ) : (
                          <div>
                            <p
                              className="comment-ft-container"
                              dangerouslySetInnerHTML={{
                                __html: values.evaluateeComment,
                              }}
                            />
                          </div>
                        )}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </Paper>


          </div>
        </Form>
      )}
    </Formik>
  );
};

export default UnlicensedEvaluationForm;
